import { Check, ChevronLeft, ChevronRight, Edit3 } from "lucide-react";
import { PatientDetailsCardProps } from "./types";
import patientAvtar from "../../assets/images/default_avatar.jpg";
import { formatAge } from "../../shared/AddNewPatientModal";
import { useState, useRef, useEffect } from "react";
import { AddNewPatientModal } from "../../shared/AddNewPatientModal";


export function PatientDetailsCard({
  isVerified,
  patientDetails,
  setPatientDetails,
  selectedDoctor,
  setSelectedDoctor,
  isNewPatient = false,
  previouslyConsultedDoctors,
  previousFamilyDetails,
  selectedFamilyMember,
  setSelectedFamilyMember,
}: PatientDetailsCardProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // Slider state for doctors
  const [doctorScrollPosition, setDoctorScrollPosition] = useState(0);
  const [familyScrollPosition, setFamilyScrollPosition] = useState(0);
  const doctorSliderRef = useRef<HTMLDivElement>(null);
  const familySliderRef = useRef<HTMLDivElement>(null);
  const [showDoctorLeftArrow, setShowDoctorLeftArrow] = useState(false);
  const [showDoctorRightArrow, setShowDoctorRightArrow] = useState(false);
  const [showFamilyLeftArrow, setShowFamilyLeftArrow] = useState(false);
  const [showFamilyRightArrow, setShowFamilyRightArrow] = useState(false);

  // const patientId = patientDetails?.id

  // Filter out duplicate doctors (same name and date)
  const filteredDoctors = previouslyConsultedDoctors
    ? previouslyConsultedDoctors.filter(
        (doctor, index, self) =>
          index ===
          self.findIndex(
            (d) =>
              d.doctorName === doctor.doctorName &&
              d.consultationDate.split("T")[0] ===
                doctor.consultationDate.split("T")[0]
          )
      )
    : [];

  // Check if doctor arrows should be visible
  useEffect(() => {
    const checkDoctorArrows = () => {
      const slider = doctorSliderRef.current;
      if (!slider || !filteredDoctors?.length) return;

      setShowDoctorLeftArrow(doctorScrollPosition > 0);
      setShowDoctorRightArrow(
        doctorScrollPosition < slider.scrollWidth - slider.clientWidth
      );
    };

    checkDoctorArrows();
    window.addEventListener("resize", checkDoctorArrows);
    return () => window.removeEventListener("resize", checkDoctorArrows);
  }, [doctorScrollPosition, filteredDoctors]);

  // Check if family arrows should be visible
  useEffect(() => {
    const checkFamilyArrows = () => {
      const slider = familySliderRef.current;
      if (!slider || !previousFamilyDetails?.length) return;

      setShowFamilyLeftArrow(familyScrollPosition > 0);
      setShowFamilyRightArrow(
        familyScrollPosition < slider.scrollWidth - slider.clientWidth
      );
    };

    checkFamilyArrows();
    window.addEventListener("resize", checkFamilyArrows);
    return () => window.removeEventListener("resize", checkFamilyArrows);
  }, [familyScrollPosition, previousFamilyDetails]);

  // Handle scroll events for doctors
  const handleDoctorScroll = () => {
    if (!doctorSliderRef.current) return;
    setDoctorScrollPosition(doctorSliderRef.current.scrollLeft);
  };

  // Handle scroll events for family
  const handleFamilyScroll = () => {
    if (!familySliderRef.current) return;
    setFamilyScrollPosition(familySliderRef.current.scrollLeft);
  };

  // Scroll left and right functions for doctors
  const scrollDoctorLeft = () => {
    if (!doctorSliderRef.current) return;
    const newPosition = Math.max(0, doctorScrollPosition - 200);
    doctorSliderRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
  };

  const scrollDoctorRight = () => {
    if (!doctorSliderRef.current) return;
    const newPosition = Math.min(
      doctorSliderRef.current.scrollWidth - doctorSliderRef.current.clientWidth,
      doctorScrollPosition + 200
    );
    doctorSliderRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
  };

  // Scroll left and right functions for family
  const scrollFamilyLeft = () => {
    if (!familySliderRef.current) return;
    const newPosition = Math.max(0, familyScrollPosition - 200);
    familySliderRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
  };

  const scrollFamilyRight = () => {
    if (!familySliderRef.current) return;
    const newPosition = Math.min(
      familySliderRef.current.scrollWidth - familySliderRef.current.clientWidth,
      familyScrollPosition + 200
    );
    familySliderRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
  };

  const profileImageSrc =
    patientDetails?.profileImage instanceof File
      ? URL.createObjectURL(patientDetails.profileImage)
      : patientDetails?.profileImage || patientAvtar;

  if (!isVerified || !patientDetails) return null;

  return (
    <div className="mt-6 space-y-6">
      <div className="border border-gray-300/70 rounded-md px-4 py-3">
        {/* Patient details */}

        <div onClick={(e) => {
           e.stopPropagation();
           e.preventDefault();
          setSelectedFamilyMember(null)}} className={`cursor-pointer flex justify-between border ${!selectedFamilyMember ? 'border-emerald-600' : 'border-gray-300/70'} rounded-md px-4 py-3`}>
          <div>
            <div className="flex items-center justify-start gap-2">
              <div className="w-16 h-16 overflow-hidden rounded-md">
                <img
                  src={profileImageSrc}
                  alt={patientDetails.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-col ">
                <h3 className="text-lg font-medium text-gray-900">
                  {patientDetails.name}
                </h3>
                <p className="text-sm text-gray-500 font-semibold">
                  <span>{patientDetails?.mobileNumber ?? "1234567890"}</span>
                  <span className="mx-8"> {patientDetails.gender} </span>
                  <span>Age: {formatAge(patientDetails.age)}</span>
                </p>
              </div>
            </div>
          </div>
          <div>
            <button onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setIsEditModalOpen(true)
            }}>
              <Edit3
                className="text-green-600 cursor-pointer mt-3"
                onClick={() => console.log("Edit Clicked:", patientDetails)}
              />
            </button>
          </div>
        </div>

        {/* Previously Consulted Doctors Section */}
        {!isNewPatient && filteredDoctors?.length > 0 && (
          <div className="my-3">
            <h4 className="text-sm font-medium text-secondary">
              Previously Consulted Doctors
            </h4>

            <div className="my-3 relative">
              {/* Left arrow */}
              {showDoctorLeftArrow && (
                <button
                  onClick={scrollDoctorLeft}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 rounded-full p-1 shadow-md border border-gray-200"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
              )}

              {/* Right arrow */}
              {showDoctorRightArrow && (
                <button
                  onClick={scrollDoctorRight}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 rounded-full p-1 shadow-md border border-gray-200"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              )}

              {/* Doctors slider */}
              <div
                ref={doctorSliderRef}
                onScroll={handleDoctorScroll}
                className="flex items-center overflow-x-auto scrollbar-hide gap-2 scroll-smooth"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {filteredDoctors.map((doctor) => (
                  <button
                    key={doctor.doctorId}
                    onClick={() => setSelectedDoctor(doctor.doctorId)}
                    className="p-2 flex-shrink-0 flex items-center justify-between transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 rounded-full overflow-hidden relative">
                        <img
                          src={doctor.doctorProfileImage}
                          alt={doctor.doctorName}
                          className="w-full h-full object-cover"
                        />
                        {selectedDoctor === doctor.doctorId && (
                          <div className="flex items-center justify-center z-10 absolute top-0 left-0 w-full h-full bg-white/25">
                            <span className="bg-emerald-600 flex items-center justify-center w-6 h-6 rounded-full border-2 border-white">
                              <Check className="w-3 h-3 text-white" />
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-gray-900">
                          {doctor.doctorName}
                        </div>
                        <span className="text-sm text-gray-500">
                          {doctor.consultationDate.split("T")[0]}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Family Members Section */}
        {previousFamilyDetails && previousFamilyDetails.length > 0 && (
          <div className="my-3">
            <h4 className="text-sm font-medium text-secondary">
              Family Members
            </h4>

            <div className="my-3 relative">
              {/* Left arrow */}
              {showFamilyLeftArrow && (
                <button
                  onClick={scrollFamilyLeft}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 rounded-full p-1 shadow-md border border-gray-200"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
              )}

              {/* Right arrow */}
              {showFamilyRightArrow && (
                <button
                  onClick={scrollFamilyRight}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 rounded-full p-1 shadow-md border border-gray-200"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              )}

              {/* Family Members slider */}
              <div
                ref={familySliderRef}
                onScroll={handleFamilyScroll}
                className="flex items-center overflow-x-auto scrollbar-hide gap-2 scroll-smooth"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {previousFamilyDetails.map((familyDetails) => (
                  <button
                    key={familyDetails.id}
                    onClick={() => setSelectedFamilyMember(familyDetails)}
                    className={`rounded-md border p-2 flex-shrink-0 flex items-center justify-between transition-colors ${
                      familyDetails.id === selectedFamilyMember?.id ?
                      "border-emerald-600 " : "border-gray-300/70"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 rounded-full overflow-hidden relative">
                        <img
                          src={familyDetails.profile_image || patientAvtar}
                          alt={familyDetails.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-gray-900 flex items-center justify-start gap-4">
                          <span>{familyDetails.name}</span>
                          <span>•</span>
                          <span>{familyDetails.relation}</span>
                        </div>
                        <div className="text-sm text-gray-500 space-x-2">
                          <span>{familyDetails.mobile_number}</span>
                          <span>•</span>
                          <span>{familyDetails.gender}</span>
                          <span>•</span>
                          <span>Age: {formatAge(familyDetails.age)}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {isEditModalOpen && (
        <AddNewPatientModal
          mobileNumber={patientDetails.mobileNumber}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={(updatedPatient) => {
            console.log("Updated Patient:", updatedPatient);
            setPatientDetails(updatedPatient);
            setIsEditModalOpen(false);
          }}
          initialData={patientDetails}
          isVerifiedProps={true}
        />
      )}
    </div>
  );
}
